import { PublicLayout } from '@/layouts/PublicLayout';
import { Button } from '@/components/Button';
import { useNavigate } from 'react-router-dom';
import {
  Eye, Shield, Users, BarChart3, FileText, Calendar,
  MessageSquare, Search, Brain, Lock, Globe
} from 'lucide-react';

export default function FeaturesPage() {
  const navigate = useNavigate();

  const features = [
    {
      category: 'AI-Powered Screening',
      icon: <Brain className="h-6 w-6" />,
      items: [
        {
          title: 'Smart Resume Parsing',
          description: 'Automatically extract and structure information from resumes in any format.',
          icon: <FileText className="h-5 w-5" />,
        },
        {
          title: 'AI Candidate Matching',
          description: 'Match candidates to jobs based on skills, experience, and cultural fit.',
          icon: <Search className="h-5 w-5" />,
        },
        {
          title: 'Explainable Scoring',
          description: 'See exactly why each candidate received their score with detailed breakdowns.',
          icon: <Eye className="h-5 w-5" />,
        },
      ],
    },
    {
      category: 'Bias Detection & Compliance',
      icon: <Shield className="h-6 w-6" />,
      items: [
        {
          title: 'Automated Bias Audits',
          description: 'Continuous monitoring for disparate impact and statistical parity.',
          icon: <BarChart3 className="h-5 w-5" />,
        },
        {
          title: 'GDPR Compliance',
          description: 'Built-in data protection with consent management and right to erasure.',
          icon: <Lock className="h-5 w-5" />,
        },
        {
          title: 'Audit Trails',
          description: 'Complete record of all hiring decisions and AI recommendations.',
          icon: <FileText className="h-5 w-5" />,
        },
      ],
    },
    {
      category: 'Collaboration Tools',
      icon: <Users className="h-6 w-6" />,
      items: [
        {
          title: 'Team Workflows',
          description: 'Collaborative hiring with role-based permissions and approvals.',
          icon: <Users className="h-5 w-5" />,
        },
        {
          title: 'Interview Scheduling',
          description: 'Automated scheduling with calendar integration and reminders.',
          icon: <Calendar className="h-5 w-5" />,
        },
        {
          title: 'Internal Messaging',
          description: 'Communicate with your team directly within the platform.',
          icon: <MessageSquare className="h-5 w-5" />,
        },
      ],
    },
    {
      category: 'Analytics & Reporting',
      icon: <BarChart3 className="h-6 w-6" />,
      items: [
        {
          title: 'Hiring Metrics',
          description: 'Track time-to-hire, cost-per-hire, and quality-of-hire metrics.',
          icon: <BarChart3 className="h-5 w-5" />,
        },
        {
          title: 'Diversity Reports',
          description: 'Monitor diversity metrics and identify improvement opportunities.',
          icon: <Globe className="h-5 w-5" />,
        },
        {
          title: 'Custom Dashboards',
          description: 'Build dashboards tailored to your organization\'s needs.',
          icon: <BarChart3 className="h-5 w-5" />,
        },
      ],
    },
  ];

  const integrations = [
    'LinkedIn', 'Indeed', 'Greenhouse', 'Workday', 'BambooHR',
    'Google Calendar', 'Microsoft Teams', 'Slack', 'Zoom'
  ];

  return (
    <PublicLayout>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-50 via-white to-secondary-50 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Everything You Need for{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-secondary-600">
              Fair Hiring
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-10">
            Powerful features designed for transparency, compliance, and efficiency.
            Built by hiring experts, for hiring professionals.
          </p>
          <Button size="lg" onClick={() => navigate('/signup')}>
            Start Free Trial
          </Button>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="space-y-20">
            {features.map((category) => (
              <div key={category.category}>
                <div className="flex items-center space-x-3 mb-8">
                  <div className="rounded-lg bg-primary-100 p-3 text-primary-600">
                    {category.icon}
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                    {category.category}
                  </h2>
                </div>
                <div className="grid gap-6 md:grid-cols-3">
                  {category.items.map((item) => (
                    <div
                      key={item.title}
                      className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow border border-gray-200"
                    >
                      <div className="rounded-lg bg-primary-100 w-10 h-10 flex items-center justify-center text-primary-600 mb-4">
                        {item.icon}
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                      <p className="text-gray-600">{item.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Integrations */}
      <section className="py-24 bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Integrates with Your Tools
            </h2>
            <p className="text-lg text-gray-600">
              Connect with the platforms you already use
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-6">
            {integrations.map((integration) => (
              <div
                key={integration}
                className="bg-white rounded-lg px-6 py-3 shadow-sm border border-gray-200 text-gray-700 font-medium"
              >
                {integration}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-white">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-gray-600 mb-10">
            Try all features free for 14 days. No credit card required.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" onClick={() => navigate('/signup')}>
              Start Free Trial
            </Button>
            <Button size="lg" variant="outline" onClick={() => navigate('/pricing')}>
              View Pricing
            </Button>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
